import React, { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Menu, Transition } from "@headlessui/react";
import { ArrowPathIcon, DocumentDuplicateIcon, PencilSquareIcon, PlusIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon, ChevronUpIcon, ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import { Button } from "../forms/Buttons/Button";
import { DIFFICULTY_LEVELS, DIFFICULTY_POINTS, DifficultyBadge } from "../Difficulty/Difficulty";
import HelpButton from "../Help/HelpButton";
import MatrixGuide from "../Help/guides/MatrixGuide";
import OverflowMenu from "../ui/OverflowMenu";
import { getFolderMatrix, getTopics, saveFolderMatrix } from "../../services/questionService";
import { getApiErrorCode, getApiErrorMessage } from "../../utils/apiError";
import { isCanceledError } from "../../utils/examError";
import { fetchAllPages } from "../../utils/fetchAllPages";
import { canAssignIn, canEditMatrix } from "../../utils/folders";
import { useMediaQuery } from "../../pages/Exams/TestBuilder/ui";
import AddRowForm from "./matrix/AddRowForm";
import CopyMatrixDialog, { copySources, hasRows, useSourceSummaries } from "./matrix/CopyMatrixDialog";
import MatrixEmpty from "./matrix/MatrixEmpty";
import useLeaveGuard from "./matrix/useLeaveGuard";
import TaskDialog from "./tasks/TaskDialog";
import {
  BankStatus,
  ClassSummary,
  ClassTabs,
  Dash,
  PlanBar,
  QuarterHeading,
  RowName,
  Spinner,
  Stepper,
  WeekText,
  addLinkClass,
  cardClass,
  focusRing,
  linkButtonClass,
  secondaryButtonClass,
  selectClass,
  tasksAndPoints,
} from "./matrix/MatrixParts";
import {
  GRADES,
  MAX_ROWS,
  MAX_WEEK,
  QUARTERS,
  ROMAN,
  changeDetails,
  compareGrades,
  countOf,
  draftRow,
  errorField,
  gradeLabel,
  gradeOf,
  gradeParam,
  hasHours,
  isSchoolGrade,
  isV2,
  levelsText,
  matrixChanges,
  plannedOf,
  pointsOf,
  quarterLabel,
  quarterRoles,
  readMatrix,
  rowKey,
  rowName,
  signature,
  sortRows,
  standardGaps,
  subtopicsOf,
  templateGaps,
  toNumber,
  toPayload,
  topicMarks,
  topicName,
  trimName,
  validateDraft,
} from "./matrix/matrixModel";

const iconButtonClass = `inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-400 transition duration-200 enabled:hover:bg-red-50 enabled:hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40 max-md:h-11 max-md:w-11 ${focusRing}`;
// A row's topic button: «Өзгерту» quiet, «Тақырып таңдау» (a row without a topic) blue; 44px tall on phones.
const topicButtonClass = (blue) =>
  `inline-flex shrink-0 items-center rounded-lg px-1.5 py-0.5 text-xs font-medium transition duration-200 disabled:cursor-not-allowed disabled:opacity-40 max-md:min-h-[2.75rem] max-md:px-3 max-md:text-sm ${
    blue ? "text-blue-700 enabled:hover:bg-blue-50 enabled:hover:text-blue-800" : "text-slate-500 enabled:hover:bg-slate-100 enabled:hover:text-slate-800"
  } ${focusRing}`;
const headCell = "px-3 py-2 text-xs font-semibold text-slate-500";
// The save conflict's second way out: quiet text on the panel's amber (a full-width line under the main button on
// phones).
const conflictQuietClass = `inline-flex min-h-[2.5rem] items-center justify-center rounded-lg px-3 py-1.5 text-center text-sm font-medium text-amber-900 transition duration-200 enabled:hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-[2.75rem] max-md:w-full ${focusRing}`;
// What the edit mode focuses stays clear of the sticky plan bar (its height in --plan-bar, and a gap) and, on
// phones, of the sticky Cancel / Save bar.
const focusClearance = "[&_[data-focus]]:scroll-mt-[calc(var(--plan-bar,0px)+2rem)] max-md:[&_[data-focus]]:scroll-mb-24";

// A grade's quarters as sections: in the edit mode all four (rows are added to one), then rows without a quarter;
// when reading, only the quarters that have rows.
function quarterGroups(rows, editing) {
  const byQuarter = new Map();
  rows.forEach((row) => {
    const quarter = row.quarter || null;
    if (!byQuarter.has(quarter)) byQuarter.set(quarter, []);
    byQuarter.get(quarter).push(row);
  });
  const quarters = editing ? [...QUARTERS] : QUARTERS.filter((q) => byQuarter.has(q));
  if (byQuarter.has(null)) quarters.push(null);
  return quarters.map((quarter) => ({ quarter, rows: byQuarter.get(quarter) || [] }));
}

const hoursOf = (rows) => rows.reduce((sum, row) => sum + toNumber(row.hours), 0);

// A grade's summary when the server sends no `grades` (an older backend): the same sums from its rows.
function gradeInfoOf(rows) {
  const planned = plannedOf(rows);
  const roles = quarterRoles(rows.map((row) => row.quarter));
  return {
    planned: { ...planned, count: countOf(planned), points: pointsOf(planned) },
    approved: { count: rows.reduce((sum, row) => sum + countOf(row.approved), 0) },
    quarters: quarterGroups(rows, false).map(({ quarter, rows: list }) => ({
      quarter,
      role: roles[quarter] || null,
      planned: plannedOf(list),
      hours: hasHours(list) ? hoursOf(list) : null,
    })),
  };
}

// "+ Add a grade" of the edit mode, a text link after the tabs: the grades the subject has topics for and the draft
// has no tab of yet.
function AddGradeMenu({ grades, onAdd, disabled }) {
  const [t] = useTranslation();
  return (
    // Under sm it takes a line of its own below the sideways-scrolling tabs: its "+" lines up with them.
    <Menu as="div" className="relative max-sm:-ml-2">
      <Menu.Button disabled={disabled} className={addLinkClass}>
        <PlusIcon className="h-4 w-4" aria-hidden="true" />
        {t("folders.matrix.add_class")}
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100 motion-reduce:transition-none"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-75 motion-reduce:transition-none"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <Menu.Items className="absolute left-0 z-30 mt-2 max-h-72 w-52 overflow-auto rounded-xl bg-white p-1 shadow-lg ring-1 ring-slate-200 focus:outline-none">
          {grades.length ? (
            grades.map((grade) => (
              <Menu.Item key={grade}>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={() => onAdd(grade)}
                    className={`flex w-full rounded-lg px-3 py-2 text-left text-sm ${active ? "bg-slate-100 text-slate-900" : "text-slate-700"}`}
                  >
                    {gradeLabel(t, grade)}
                  </button>
                )}
              </Menu.Item>
            ))
          ) : (
            <p className="px-3 py-2 text-sm text-slate-500">{t("folders.matrix.add_class_none")}</p>
          )}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

/**
 * The Matrix tab of a subject folder (`folder` is a subject of GET question_folders/, `folders` the whole list):
 * each grade's plan, a row a week: its topic (whole, or some of its subtopics that share the row's A, B and C) and
 * how many A, B and C tasks it gets, by quarter and week, next to what the bank has approved. A row may wait for its
 * topic (the weeks of a «БТС-1»…«БТС-4» folder's template). Everyone reads it, a grade at a time; its moderator and
 * admins (`can_edit`) edit a draft of all grades at once and save it whole (with the revision it started from: 409
 * matrix_changed shows what changed meanwhile), or copy it from another folder of the same subject. A pre-v2 server's
 * matrix (no `template`) is only read. While the draft has unsaved changes, leaving it asks first (useLeaveGuard);
 * `onDirtyChange(dirty)` tells the page, for what it switches itself.
 * The address's ?grade= opens that grade's tab once (a subject variant's link to its weeks), then leaves the address.
 * The header keeps one main button (Edit, or Save with a quiet Cancel) and "⋯" for copying; the "?" after the title
 * (on the empty page, its "How it works" link) opens MatrixGuide.
 */
export default function FolderMatrix({ folder, folders, onDirtyChange }) {
  const [t] = useTranslation();
  const uuid = folder?.uuid || "";
  const subjectUuid = folder?.subject?.uuid || "";
  const titleId = useId();
  const tabsId = useId();
  const wide = useMediaQuery("(min-width: 768px)");
  // ?grade= (a subject variant's «Матрицаны ашу» carries the variant's grade): that grade's tab opens when the matrix
  // has it; a grade it lacks leaves the first tab. Once read, it leaves the address (below).
  const [searchParams, setSearchParams] = useSearchParams();
  const gradeAsked = searchParams.get("grade");
  const urlGrade = gradeParam(gradeAsked);
  const urlGradeRef = useRef(urlGrade);
  urlGradeRef.current = urlGrade;
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [grade, setGrade] = useState(urlGrade);
  const [editing, setEditing] = useState(false);
  // Draft rows: {id, order, grade, topic | null, subtopics, quarter, week, hours and planned {A, B, C} as typed,
  // approved}.
  const [draft, setDraft] = useState([]);
  const [baseline, setBaseline] = useState("");
  // The matrix the draft started from: its revision goes with the save, its rows show what a 409 changed.
  const [base, setBase] = useState(null);
  // Grades of the draft that have no rows (added from the menu, or emptied): a grade exists through its rows.
  const [extraGrades, setExtraGrades] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  // The row the server refused (400 invalid_matrix): {id, message}.
  const [serverError, setServerError] = useState(null);
  // Where the add form is open: {grade, quarter}.
  const [addFor, setAddFor] = useState(null);
  // The row whose topic the change form (under it) changes.
  const [changeFor, setChangeFor] = useState(null);
  // A save refused with 409 matrix_changed: {fresh (the matrix now), changes}.
  const [conflict, setConflict] = useState(null);
  const [copyOpen, setCopyOpen] = useState(false);
  // Reads the copy sources' matrices again (when the copy dialog opens).
  const [sourcesAttempt, setSourcesAttempt] = useState(0);
  const [topics, setTopics] = useState(null);
  // Phones: the row whose fields are open (the others show a summary).
  const [openRow, setOpenRow] = useState(null);
  const [planBarHeight, setPlanBarHeight] = useState(0);
  // "heading", "conflict", "save-error", "add:<quarter>", "<row id>:A" / ":quarter" / ":week" / ":topic" / ":row":
  // focused after the render that brings it.
  const [focusTarget, setFocusTarget] = useState(null);
  const nextId = useRef(0);
  const nextOrder = useRef(0);
  const uuidRef = useRef(uuid);
  const abortRef = useRef(null);
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const planBarRef = useRef(null);
  const topicsCache = useRef(null);
  // A topic's subtopics as the add form read them (topic uuid -> list), for the next form.
  const subtopicsCache = useRef(new Map());
  // Tasks (TZ 2.2): the row a task is being given for, by the subject's moderator or an admin.
  const canAssign = canAssignIn(folder?.role);
  const [taskRow, setTaskRow] = useState(null);

  const load = useCallback(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setLoadError(null);
    getFolderMatrix(uuid, { signal: controller.signal })
      .then((data) => {
        if (!controller.signal.aborted) setMatrix(readMatrix(data));
      })
      .catch((err) => {
        if (!isCanceledError(err) && !controller.signal.aborted) setLoadError(err);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
  }, [uuid]);

  // Another subject: its own matrix (on the address's grade), and whatever was being edited is dropped.
  useEffect(() => {
    uuidRef.current = uuid;
    setMatrix(null);
    setGrade(urlGradeRef.current);
    setEditing(false);
    setDraft([]);
    setBase(null);
    setExtraGrades([]);
    setSaving(false);
    setSaveError("");
    setServerError(null);
    setAddFor(null);
    setChangeFor(null);
    setConflict(null);
    setCopyOpen(false);
    setTopics(null);
    setOpenRow(null);
    if (!uuid) return undefined;
    load();
    return () => abortRef.current?.abort();
  }, [uuid, load]);

  // A link that asks for another grade while the matrix shows (the address changes, the page stays): its tab.
  useEffect(() => {
    if (urlGrade !== null) setGrade(urlGrade);
  }, [urlGrade]);

  useEffect(() => {
    if (!focusTarget) return;
    const find = (key) => {
      const el = sectionRef.current?.querySelector(`[data-focus="${key}"]`);
      return el && !el.disabled ? el : null;
    };
    // A phone's folded row has only its summary to take the focus; a field locked by the row's open change form (or
    // hidden by it) leaves it to the row's topic button.
    const id = focusTarget.split(":")[0];
    const el = focusTarget === "heading" ? null : find(focusTarget) || find(`${id}:row`) || find(`${id}:topic`);
    // A field of a phone's row brings the whole card into view, its name over the field.
    const card = !wide && el && /:(A|quarter|week|hours|topic)$/.test(focusTarget) ? el.closest("li") : null;
    (el || headingRef.current)?.focus(card ? { preventScroll: true } : undefined);
    card?.scrollIntoView({ block: "nearest" });
    setFocusTarget(null);
  }, [focusTarget, wide]);

  const points = useMemo(() => ({ ...DIFFICULTY_POINTS, ...(matrix?.points || {}) }), [matrix]);
  const standard = matrix?.standard || null;
  // Only a v2 server's matrix is edited or copied here; its template exists in the «БТС-1»…«БТС-4» folders.
  const v2 = isV2(matrix);
  const canEdit = !!matrix?.can_edit && v2;
  const template = (v2 && matrix?.template) || null;
  const savedRows = useMemo(() => (matrix?.rows || []).map((row, index) => ({ ...row, order: index })), [matrix]);
  const sources = useMemo(() => copySources(folder, folders), [folder, folders]);
  // Copying is offered from the sources whose matrix has rows.
  const summaries = useSourceSummaries(sources, canEdit && sources.length > 0, sourcesAttempt);
  const filled = sources.filter(({ folder: f }) => hasRows(summaries[f.uuid]));
  const copyable = filled.length > 0;

  const rows = editing ? draft : savedRows;
  // Topics of a grade in the matrix that have the same name: their rows show what tells them apart (topicMarks).
  const lookAlikes = useMemo(() => {
    const byName = new Map();
    rows.forEach((row) => {
      if (!row.topic) return;
      const key = `${gradeOf(row)}|${trimName(row.topic.name)}`;
      if (!byName.has(key)) byName.set(key, new Set());
      byName.get(key).add(row.topic.uuid);
    });
    return new Set([...byName.values()].filter((same) => same.size > 1).flatMap((same) => [...same]));
  }, [rows]);

  // The subject's topics: for the grades a new matrix or a new tab can have (the add form reuses them), and for the
  // codes of look-alike topics.
  const needTopics = (canEdit && (editing || savedRows.length === 0)) || lookAlikes.size > 0;
  useEffect(() => {
    if (!needTopics || !subjectUuid) return undefined;
    if (topicsCache.current?.subject === subjectUuid) {
      setTopics(topicsCache.current.list);
      return undefined;
    }
    const controller = new AbortController();
    fetchAllPages((page) => getTopics({ params: { subject: subjectUuid, ...page }, signal: controller.signal }))
      .then((list) => {
        topicsCache.current = { subject: subjectUuid, list };
        setTopics(list);
      })
      .catch((err) => {
        if (!isCanceledError(err)) setTopics([]);
      });
    return () => controller.abort();
  }, [needTopics, subjectUuid]);
  const topicGrades = useMemo(
    () => (topics?.length ? [...new Set(topics.map((topic) => (Number.isInteger(topic.grade) ? topic.grade : 0)))].sort(compareGrades) : GRADES),
    [topics]
  );
  const catalogMarks = useMemo(() => topicMarks(t, topics), [t, topics]);
  const markOf = (row) => (row.topic && lookAlikes.has(row.topic.uuid) ? catalogMarks.get(row.topic.uuid) || "" : "");
  // "10-сынып, III тоқсан, 2-апта": where a row is, for the messages that name it by its topic.
  const placeOf = (row) =>
    [gradeLabel(t, gradeOf(row)), row.quarter ? quarterLabel(t, row.quarter) : null, row.week ? t("folders.matrix.week_n", { n: row.week }) : null]
      .filter(Boolean)
      .join(", ");

  const grades = useMemo(() => {
    const present = new Set(rows.map(gradeOf));
    if (editing) extraGrades.forEach((g) => present.add(g));
    return [...present].sort(compareGrades);
  }, [rows, editing, extraGrades]);
  const selected = grades.includes(grade) ? grade : grades.length ? grades[0] : null;
  const gradeRows = useMemo(() => sortRows(rows.filter((row) => gradeOf(row) === selected)), [rows, selected]);
  const roles = useMemo(() => quarterRoles(gradeRows.map((row) => row.quarter)), [gradeRows]);
  const rowErrors = useMemo(() => (editing ? validateDraft(draft, t) : new Map()), [editing, draft, t]);
  // A grade tab added without rows saves nothing, so it changes nothing either.
  const dirty = editing && signature(draft) !== baseline;
  const full = draft.length >= MAX_ROWS;

  useLeaveGuard(dirty ? t("folders.matrix.discard_confirm") : null);
  useEffect(() => {
    if (!onDirtyChange) return undefined;
    onDirtyChange(dirty);
    return () => onDirtyChange(false);
  }, [dirty, onDirtyChange]);

  // The address's grade is used once (as the bank's link filters are): it leaves the address, so a reload, another
  // subject or a later tab keep to the grade chosen here. Unsaved changes would make that move ask (useLeaveGuard):
  // it waits for them meanwhile.
  useEffect(() => {
    if (gradeAsked === null || dirty) return;
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete("grade");
        return params;
      },
      { replace: true }
    );
  }, [gradeAsked, dirty, setSearchParams]);

  // The sticky plan bar's height, so a focused field is scrolled below it (focusClearance).
  const planShown = editing && selected !== null;
  useEffect(() => {
    const el = planBarRef.current;
    if (!planShown || !el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => setPlanBarHeight(el.offsetHeight));
    observer.observe(el);
    return () => observer.disconnect();
  }, [planShown]);

  const newId = () => {
    nextId.current += 1;
    return `row-${nextId.current}`;
  };

  // Rows without a topic for the template's weeks grade `g` lacks among `list`, at the template's counts and with no
  // hours (as many as the matrix has room for).
  const templateRowsFor = (g, list) =>
    templateGaps(
      template,
      list.filter((row) => gradeOf(row) === g)
    )
      .slice(0, Math.max(0, MAX_ROWS - list.length))
      .map((week) => {
        nextOrder.current += 1;
        return {
          id: newId(),
          order: nextOrder.current,
          grade: g,
          topic: null,
          subtopics: [],
          quarter: week.quarter,
          week: week.week,
          hours: "",
          planned: Object.fromEntries(DIFFICULTY_LEVELS.map((level) => [level, String(toNumber(week.planned?.[level]))])),
          approved: null,
        };
      });

  // The edit mode: a draft of every grade's rows; `startGrade` opens a grade that has none yet (a new matrix), with
  // `fill` at the template's weeks (the first one's «Тақырып таңдау» takes the focus). Nothing is saved until Save.
  const startEdit = (startGrade = null, fill = false) => {
    const list = savedRows.map((row, index) => draftRow(row, newId(), index));
    nextOrder.current = list.length;
    const made = fill && isSchoolGrade(startGrade) ? templateRowsFor(startGrade, list) : [];
    const first = sortRows(made)[0];
    setDraft([...list, ...made]);
    setBaseline(signature(list));
    setBase(matrix);
    setExtraGrades(startGrade !== null && !made.length && !list.some((row) => gradeOf(row) === startGrade) ? [startGrade] : []);
    if (startGrade !== null) setGrade(startGrade);
    setEditing(true);
    setSaveError("");
    setServerError(null);
    setAddFor(null);
    setChangeFor(null);
    setConflict(null);
    setOpenRow(first ? first.id : null);
    setFocusTarget(first ? `${first.id}:topic` : startGrade !== null ? `add:${QUARTERS[0]}` : "heading");
  };

  const finishEdit = () => {
    setEditing(false);
    setDraft([]);
    setExtraGrades([]);
    setBase(null);
    setAddFor(null);
    setChangeFor(null);
    setServerError(null);
    setSaveError("");
    setConflict(null);
    setOpenRow(null);
    setFocusTarget("heading");
  };

  const cancel = () => {
    if (saving) return;
    if (dirty && !window.confirm(t("folders.matrix.discard_confirm"))) return;
    finishEdit();
  };

  const updateRow = (id, change) => {
    setDraft((list) => list.map((row) => (row.id === id ? { ...row, ...change(row) } : row)));
    setServerError((current) => (current?.id === id ? null : current));
  };
  const setCount = (id, level, value) => updateRow(id, (row) => ({ planned: { ...row.planned, [level]: value } }));
  // Another quarter moves the row to its section (the focus follows it); no quarter takes the week too.
  const setQuarter = (id, value) => {
    const quarter = value ? Number(value) : null;
    updateRow(id, (row) => ({ quarter, week: quarter ? row.week : null }));
    setFocusTarget(`${id}:quarter`);
  };
  const setWeek = (id, value) => {
    updateRow(id, () => ({ week: value ? Number(value) : null }));
    setFocusTarget(`${id}:week`);
  };
  // Phones show one row's fields at a time; the others fold to a summary (its button keeps the focus).
  const toggleRow = (id, open) => {
    setOpenRow(open ? id : null);
    setFocusTarget(`${id}:row`);
  };

  const removeRow = (row) => {
    const siblings = gradeRows.filter((r) => (r.quarter || null) === (row.quarter || null));
    const index = siblings.findIndex((r) => r.id === row.id);
    const next = siblings[index + 1] || siblings[index - 1];
    // The grade's last row leaves its tab open (an empty grade saves nothing), so it can be planned again.
    if (!gradeRows.some((r) => r.id !== row.id)) {
      const g = gradeOf(row);
      setExtraGrades((list) => (list.includes(g) ? list : [...list, g]));
    }
    setDraft((list) => list.filter((r) => r.id !== row.id));
    setServerError((current) => (current?.id === row.id ? null : current));
    setChangeFor((current) => (current === row.id ? null : current));
    setFocusTarget(next ? `${next.id}:A` : `add:${row.quarter || QUARTERS[0]}`);
  };

  // A row's bank: a saved row of the same topic and subtopics has the server's counts; else what the form read.
  const bankOf = (row, read) => savedRows.find((saved) => rowKey(saved) === rowKey(row))?.approved || read || null;

  // The add form's row goes to its quarter: at the template's counts for a template week the grade had no row in,
  // else at 0 of every level.
  const addRow = ({ topic, subtopics, week, hours, approved, planned }, quarter) => {
    if (draft.length >= MAX_ROWS) return;
    nextOrder.current += 1;
    const row = {
      id: newId(),
      order: nextOrder.current,
      grade: gradeOf({ topic }),
      topic,
      subtopics,
      quarter,
      week: quarter ? week : null,
      hours,
      planned: Object.fromEntries(DIFFICULTY_LEVELS.map((level) => [level, String(toNumber(planned?.[level]))])),
    };
    setDraft((list) => [...list, { ...row, approved: bankOf(row, approved) }]);
    setAddFor(null);
    setSaveError("");
    setOpenRow(row.id);
    setFocusTarget(`${row.id}:A`);
  };

  // The change form replaces a row's topic, subtopics, week and hours; its counts stay. The focus goes back to it.
  const changeRow = (id, { topic, subtopics, week, hours, approved }) => {
    updateRow(id, (row) => {
      const next = { ...row, grade: gradeOf({ topic }), topic, subtopics, week: row.quarter ? week : null, hours };
      return { ...next, approved: bankOf(next, approved) };
    });
    setChangeFor(null);
    setFocusTarget(`${id}:topic`);
  };
  const closeChange = (id) => {
    setChangeFor(null);
    setFocusTarget(`${id}:topic`);
  };
  const toggleChange = (row) => {
    if (changeFor === row.id) {
      setChangeFor(null);
      return;
    }
    setChangeFor(row.id);
    setAddFor(null);
    setOpenRow(row.id);
  };

  // The template's weeks grade `g` lacks come as rows without a topic; the first one's «Тақырып таңдау» takes the
  // focus.
  const fillTemplate = (g) => {
    const made = templateRowsFor(g, draft);
    if (!made.length) return;
    const first = sortRows(made)[0];
    setDraft((list) => [...list, ...made]);
    setAddFor(null);
    setChangeFor(null);
    setSaveError("");
    setOpenRow(first.id);
    setFocusTarget(`${first.id}:topic`);
  };

  const addGrade = (g) => {
    setExtraGrades((list) => (list.includes(g) ? list : [...list, g]));
    setGrade(g);
    setAddFor(null);
    setChangeFor(null);
    setFocusTarget(`add:${QUARTERS[0]}`);
  };

  // A row's error takes the focus to the field it is about (errorField): the week goes to the quarter while the row
  // has none.
  const errorFocus = (row, field) => `${row.id}:${field === "week" && !row.quarter ? "quarter" : field}`;

  // `from`: the matrix the draft is saved over. Its revision goes along, and a 409 is told against its rows.
  const save = async (from = base) => {
    if (saving) return;
    if (draft.length > MAX_ROWS || rowErrors.size) {
      setSaveError(draft.length > MAX_ROWS ? t("folders.matrix.max_rows", { max: MAX_ROWS }) : t("folders.matrix.fix_errors"));
      const first = sortRows(draft).find((row) => rowErrors.has(row.id));
      if (first) {
        setGrade(gradeOf(first));
        setFocusTarget(errorFocus(first, rowErrors.get(first.id).field));
      }
      return;
    }
    if (!draft.length && from?.rows?.length && !window.confirm(t("folders.matrix.clear_confirm"))) return;
    const requested = uuid;
    const sent = sortRows(draft);
    setSaving(true);
    setSaveError("");
    setServerError(null);
    try {
      const data = await saveFolderMatrix(requested, toPayload(sent), from?.revision);
      if (uuidRef.current !== requested) return;
      setMatrix(readMatrix(data));
      finishEdit();
      toast.success(t("folders.matrix.saved"));
    } catch (err) {
      if (uuidRef.current !== requested) return;
      const data = err?.response?.data;
      const code = getApiErrorCode(err);
      if (code === "matrix_changed") {
        // Someone saved meanwhile: read the matrix now and say what changed. The draft stays; the reading mode (after
        // a Cancel) and the next edit start from the matrix as it is now.
        try {
          const fresh = readMatrix(await getFolderMatrix(requested));
          if (uuidRef.current !== requested) return;
          setMatrix(fresh);
          setConflict({ fresh, changes: matrixChanges(from?.rows || [], fresh?.rows || []) });
          setFocusTarget("conflict");
        } catch (loadErr) {
          setSaveError(getApiErrorMessage(t, loadErr, "folders.matrix.load_failed"));
          setFocusTarget("save-error");
        }
        return;
      }
      const invalid = code === "invalid_matrix";
      const target = invalid && Number.isInteger(data?.row) ? sent[data.row] : null;
      // `reason` names what is wrong (errors.invalid_matrix_<reason>); `detail` is the server's Russian text.
      const reason = invalid && data?.reason ? t(`errors.invalid_matrix_${data.reason}`, { defaultValue: "" }) : "";
      if (target) {
        const detail = typeof data.detail === "string" ? data.detail : "";
        const field = errorField(data.reason);
        // `row` counts every grade's rows: the message says where the row is instead.
        setServerError({ id: target.id, message: reason || detail || t("folders.matrix.row_rejected_inline"), field });
        setSaveError(t("folders.matrix.row_rejected_place", { name: topicName(t, target, markOf(target)), place: placeOf(target) }));
        setGrade(gradeOf(target));
        setOpenRow(target.id);
        setFocusTarget(errorFocus(target, field));
      } else {
        setSaveError(reason || getApiErrorMessage(t, err, "folders.matrix.save_failed"));
        setFocusTarget("save-error");
      }
    } finally {
      if (uuidRef.current === requested) setSaving(false);
    }
  };

  // After a 409: save the draft over the matrix as it is now, or drop the draft and read that matrix.
  const keepMine = () => {
    const fresh = conflict.fresh;
    setBase(fresh);
    setConflict(null);
    save(fresh);
  };
  const discardMine = () => {
    const fresh = conflict.fresh;
    finishEdit();
    setMatrix(fresh);
  };

  // Copying replaces the matrix on the server: unsaved changes ask first (they stay if nothing is copied).
  const openCopy = () => {
    if (dirty && !window.confirm(t("folders.matrix.discard_confirm"))) return;
    setSourcesAttempt((n) => n + 1);
    setCopyOpen(true);
  };
  const onCopied = (data, source) => {
    // Another subject opened meanwhile keeps its own matrix.
    if (data?.folder && data.folder !== uuidRef.current) return;
    // The copy replaced the matrix (and its revision): an edit under way ends.
    if (editing) finishEdit();
    else setFocusTarget("heading");
    setMatrix(readMatrix(data));
    setCopyOpen(false);
    setGrade(null);
    toast.success(t("folders.matrix.copy_done", { name: source?.label || "" }));
  };

  if (!uuid) return null;

  const standardCount = countOf(standard);
  const tabs = grades.map((g) => {
    const planned = plannedOf(rows.filter((row) => gradeOf(row) === g));
    const count = countOf(planned);
    const check = count ? standardGaps(planned, standard) : null;
    return {
      grade: g,
      // Editing in a «БТС-N» folder: the grade's tasks against the standard («10-сынып · 19 / 20»).
      label:
        editing && standard ? (
          <>
            {t("folders.matrix.tab_target", { grade: gradeLabel(t, g), planned: count, total: standardCount })}
            <span className="sr-only"> {t("folders.matrix.tab_target_unit", { count: standardCount })}</span>
          </>
        ) : (
          `${gradeLabel(t, g)} · ${t("folders.matrix.tasks", { count })}`
        ),
      mark: check ? (check.ok ? "ok" : "off") : null,
      markText: check ? t(check.ok ? "folders.matrix.standard_ok" : "folders.matrix.standard_off") : "",
    };
  });
  const groups = quarterGroups(gradeRows, editing);
  const addableGrades = topicGrades.filter((g) => !grades.includes(g));
  // Roles go to a grade of one or two quarters (as the server says them): a grade of more says why it has none.
  const quarterCount = new Set(gradeRows.map((row) => row.quarter).filter(Boolean)).size;
  const rolesNote = quarterCount > 2 ? <p className="text-xs text-slate-500">{t("folders.matrix.roles_note", { count: quarterCount })}</p> : null;
  // The template's weeks this grade's draft lacks (a school grade's: a week without a topic needs one).
  const gaps = editing && template && isSchoolGrade(selected) ? templateGaps(template, gradeRows) : [];
  // Weeks waiting for a topic: the subject variant cannot be built until they have one (said in the edit mode too,
  // where the plan bar may already meet the standard).
  const noTopicCount = gradeRows.filter((row) => !row.topic).length;
  const noTopicLine = noTopicCount > 0 && (
    <p className="flex items-start gap-2 text-sm text-amber-800">
      <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-amber-500" aria-hidden="true" />
      {t("folders.matrix.no_topic_weeks", { count: noTopicCount })}
    </p>
  );

  // "5 тапсырма · 24 балл · 5 сағат": the hours only when some row has them.
  const quarterSummary = (list) =>
    list.length
      ? [tasksAndPoints(t, plannedOf(list), points), hasHours(list) ? t("folders.matrix.hours_n", { count: hoursOf(list) }) : null].filter(Boolean).join(" · ")
      : null;

  // Reading: a table on wide screens, cards on phones.
  // How many of a row's questions tasks give out ("Роздано: A 2 · C 1"; more than the plan is said, not refused), and
  // for the subject's moderator or an admin a task to give for it. Shown once the server counts them (`assigned`).
  const showTasks = savedRows.some((row) => row.assigned);
  const assignedCell = (row) => {
    if (!row.topic) return <Dash srText={t("folders.matrix.no_topic")} />;
    const given = DIFFICULTY_LEVELS.filter((level) => toNumber(row.assigned?.[level]) > 0);
    const over = given.some((level) => toNumber(row.assigned[level]) > toNumber(row.planned?.[level]));
    return (
      <span className="flex flex-col items-start gap-1">
        {given.length ? (
          <span className={`text-xs tabular-nums ${over ? "text-amber-800" : "text-slate-700"}`}>
            {t("folders.matrix.assigned", { levels: given.map((level) => `${level} ${toNumber(row.assigned[level])}`).join(" · ") })}
            {over && <span className="block">{t("folders.matrix.assigned_over")}</span>}
          </span>
        ) : (
          !canAssign && <Dash srText={t("folders.matrix.assigned_none")} />
        )}
        {canAssign && row.uuid && (
          <button
            type="button"
            onClick={() => setTaskRow(row)}
            aria-label={t("folders.matrix.give_task_for", { name: rowName(t, row, markOf(row)) })}
            className={`${linkButtonClass} -ml-1.5 max-md:min-h-11`}
          >
            <PlusIcon className="h-3.5 w-3.5" aria-hidden="true" />
            {t("folders.matrix.give_task")}
          </button>
        )}
      </span>
    );
  };

  const readRows = (list) =>
    wide ? (
      <div className="relative overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full min-w-[54rem] border-collapse text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th scope="col" className={`${headCell} w-24`}>
                {t("folders.matrix.col_week")}
              </th>
              <th scope="col" className={headCell}>
                {t("folders.matrix.col_topic_sub")}
              </th>
              <th scope="col" className={`${headCell} w-20 text-right`}>
                {t("folders.matrix.col_hours")}
              </th>
              {DIFFICULTY_LEVELS.map((level) => (
                <th key={level} scope="col" className={`${headCell} w-20 text-center`}>
                  <DifficultyBadge level={level} />
                </th>
              ))}
              <th scope="col" className={`${headCell} w-24 text-right`}>
                {t("folders.matrix.col_total")}
              </th>
              <th scope="col" className={`${headCell} w-20 text-right`}>
                {t("folders.matrix.col_points")}
              </th>
              <th scope="col" className={`${headCell} w-64`}>
                {t("folders.matrix.col_bank")}
              </th>
              {showTasks && (
                <th scope="col" className={`${headCell} w-40`}>
                  {t("folders.matrix.col_tasks")}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {list.map((row) => (
              <tr key={row.uuid || `${rowKey(row)}|${row.order}`} className="border-t border-slate-100 align-top">
                <td className="px-3 py-2.5 text-slate-700">
                  <WeekText week={row.week} />
                </td>
                <th scope="row" className="px-3 py-2.5 text-left font-normal">
                  <RowName row={row} mark={markOf(row)} />
                </th>
                <td className="px-3 py-2.5 text-right tabular-nums text-slate-700">{row.hours ?? <Dash srText={t("folders.matrix.hours_unset")} />}</td>
                {DIFFICULTY_LEVELS.map((level) => (
                  <td key={level} className="px-3 py-2.5 text-center font-semibold tabular-nums text-slate-900">
                    {toNumber(row.planned?.[level]) || <Dash />}
                  </td>
                ))}
                <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-slate-900">{countOf(row.planned)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{pointsOf(row.planned, points)}</td>
                <td className="px-3 py-2.5">
                  <BankStatus row={row} />
                </td>
                {showTasks && <td className="px-3 py-2.5">{assignedCell(row)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : (
      <ul className="space-y-2">
        {list.map((row) => (
          <li key={row.uuid || `${rowKey(row)}|${row.order}`} className="rounded-xl border border-slate-200 bg-white p-3">
            <RowName row={row} mark={markOf(row)} />
            <p className="mt-1 text-xs text-slate-500 empty:hidden">
              {[row.week ? t("folders.matrix.week_n", { n: row.week }) : null, row.hours !== null && row.hours !== undefined ? t("folders.matrix.hours_n", { count: row.hours }) : null]
                .filter(Boolean)
                .join(" · ")}
            </p>
            <p className="mt-1 text-sm tabular-nums text-slate-700">
              {levelsText(row.planned)} — {tasksAndPoints(t, row.planned, points)}
            </p>
            {row.topic && (
              <div className="mt-2">
                <BankStatus row={row} />
              </div>
            )}
            {showTasks && row.topic && <div className="mt-2 border-t border-slate-100 pt-2">{assignedCell(row)}</div>}
          </li>
        ))}
      </ul>
    );

  // A row's error: {message, field} of the server's 400 for it, else of the checks here (validateDraft).
  const rowErrorOf = (row) => (serverError?.id === row.id ? serverError : rowErrors.get(row.id)) || null;

  // A row's schedule and hours. A row without a topic keeps its quarter and week: it is told by them. While the row's
  // change form is open, the form sets them (its Apply would put back what was typed here meanwhile). Every field of
  // a row tells its error (`errorId`); the ones it is about are marked invalid.
  const scheduleSelects = (row, name, { large = false, error = null, errorId } = {}) => {
    const locked = saving || changeFor === row.id;
    const invalid = error?.field === "week" || undefined;
    return (
      <>
        <select
          data-focus={`${row.id}:quarter`}
          aria-label={t("folders.matrix.quarter_input", { name })}
          aria-invalid={invalid}
          aria-describedby={errorId}
          value={row.quarter ?? ""}
          disabled={locked}
          onChange={(e) => setQuarter(row.id, e.target.value)}
          className={`${selectClass} ${large ? "h-11 w-full" : "h-8 w-[4.5rem]"}`}
        >
          {QUARTERS.map((q) => (
            <option key={q} value={q}>
              {ROMAN[q]}
            </option>
          ))}
          <option value="" disabled={!row.topic}>
            —
          </option>
        </select>
        <select
          data-focus={`${row.id}:week`}
          aria-label={t("folders.matrix.week_input", { name })}
          aria-invalid={invalid}
          aria-describedby={errorId}
          value={row.week ?? ""}
          disabled={locked || !row.quarter}
          onChange={(e) => setWeek(row.id, e.target.value)}
          className={`${selectClass} ${large ? "h-11 w-full" : "h-8 w-[8rem]"}`}
        >
          <option value="" disabled={!row.topic}>
            {t("folders.matrix.week_none")}
          </option>
          {Array.from({ length: MAX_WEEK }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>
              {t("folders.matrix.week_n", { n })}
            </option>
          ))}
        </select>
      </>
    );
  };

  const hoursInput = (row, name, { large = false, error = null, errorId } = {}) => (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      data-focus={`${row.id}:hours`}
      value={row.hours}
      placeholder="—"
      disabled={saving || changeFor === row.id}
      aria-label={t("folders.matrix.hours_input", { name })}
      aria-invalid={error?.field === "hours" || undefined}
      aria-describedby={errorId}
      onChange={(e) => updateRow(row.id, () => ({ hours: e.target.value.replace(/\D/g, "").slice(0, 2) }))}
      className={`${large ? "h-11 w-full" : "h-8 w-14"} rounded-lg border-slate-300 px-1 text-center text-sm tabular-nums shadow-sm transition duration-200 hover:border-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/15 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400`}
    />
  );

  const rowError = (error, errorId) =>
    error ? (
      <p id={errorId} className="mt-1 flex items-start gap-1 text-xs font-medium text-red-700">
        <ExclamationTriangleIcon className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {error.message}
      </p>
    ) : null;

  // «Өзгерту» opens the row's change form (again: closes it); a row without a topic asks for one instead.
  const topicButton = (row, name, errorId) => {
    const choose = !row.topic;
    return (
      <button
        type="button"
        data-focus={`${row.id}:topic`}
        onClick={() => toggleChange(row)}
        disabled={saving}
        aria-expanded={changeFor === row.id}
        aria-label={t(choose ? "folders.matrix.choose_topic_label" : "folders.matrix.edit_topic_label", { name })}
        aria-describedby={errorId}
        className={topicButtonClass(choose)}
      >
        {t(choose ? "folders.matrix.choose_topic" : "folders.matrix.edit_topic")}
      </button>
    );
  };

  const changeForm = (row) => (
    <AddRowForm
      grade={gradeOf(row)}
      quarter={row.quarter}
      folderUuid={uuid}
      subjectUuid={subjectUuid}
      rows={draft}
      row={row}
      topicsCache={topicsCache}
      subtopicsCache={subtopicsCache}
      full={full}
      className=""
      onSubmit={(spec) => changeRow(row.id, spec)}
      onClose={() => closeChange(row.id)}
    />
  );

  // Editing: the same rows with their schedule, hours and counts as fields (on phones, one row's at a time); a row's
  // change form opens under it.
  const editRows = (list) =>
    wide ? (
      <div className="relative overflow-x-auto rounded-xl border border-slate-200" aria-busy={saving || undefined}>
        <table className="w-full min-w-[66rem] border-collapse text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th scope="col" className={`${headCell} w-48`}>
                {t("folders.matrix.col_quarter_week")}
              </th>
              <th scope="col" className={headCell}>
                {t("folders.matrix.col_topic_sub")}
              </th>
              <th scope="col" className={`${headCell} w-20`}>
                {t("folders.matrix.col_hours")}
              </th>
              {DIFFICULTY_LEVELS.map((level) => (
                <th key={level} scope="col" className={`${headCell} w-32 text-center`}>
                  <DifficultyBadge level={level} />
                </th>
              ))}
              <th scope="col" className={`${headCell} w-24 text-right`}>
                {t("folders.matrix.col_total")}
              </th>
              <th scope="col" className={`${headCell} w-20 text-right`}>
                {t("folders.matrix.col_points")}
              </th>
              <th scope="col" className="w-12 px-2 py-2">
                <span className="sr-only">{t("folders.matrix.actions")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {list.map((row) => {
              const mark = markOf(row);
              const name = rowName(t, row, mark);
              const error = rowErrorOf(row);
              const errorId = error ? `${titleId}-${row.id}-error` : undefined;
              return (
                <Fragment key={row.id}>
                  <tr className={`border-t border-slate-100 align-top ${error ? "bg-red-50" : ""}`}>
                    <td className="px-3 py-2">
                      <div className="flex gap-1.5">{scheduleSelects(row, name, { error, errorId })}</div>
                    </td>
                    <th scope="row" className="px-3 py-2 text-left font-normal">
                      <div className="flex items-start gap-2">
                        <div className="min-w-0 flex-1">
                          <RowName row={row} mark={mark} />
                        </div>
                        {topicButton(row, name, errorId)}
                      </div>
                      {rowError(error, errorId)}
                    </th>
                    <td className="px-3 py-2">{hoursInput(row, name, { error, errorId })}</td>
                    {DIFFICULTY_LEVELS.map((level) => (
                      <td key={level} className="px-2 py-2 text-center">
                        <Stepper
                          value={row.planned[level]}
                          onChange={(value) => setCount(row.id, level, value)}
                          level={level}
                          name={name}
                          invalid={error?.field === "A"}
                          describedBy={errorId}
                          disabled={saving}
                          bank={row.topic ? (row.approved ? toNumber(row.approved[level]) : null) : undefined}
                          focusKey={level === DIFFICULTY_LEVELS[0] ? `${row.id}:A` : undefined}
                        />
                      </td>
                    ))}
                    <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-slate-900">{countOf(row.planned)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{pointsOf(row.planned, points)}</td>
                    <td className="px-2 py-1.5">
                      <button
                        type="button"
                        onClick={() => removeRow(row)}
                        disabled={saving}
                        aria-label={t("folders.matrix.remove_row", { name })}
                        title={t("folders.matrix.remove_row", { name })}
                        className={iconButtonClass}
                      >
                        <TrashIcon className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                  {changeFor === row.id && (
                    <tr>
                      <td colSpan={DIFFICULTY_LEVELS.length + 6} className="px-3 pb-3 pt-1">
                        {changeForm(row)}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    ) : (
      <ul className="space-y-3" aria-busy={saving || undefined}>
        {list.map((row) => {
          const mark = markOf(row);
          const name = rowName(t, row, mark);
          const error = rowErrorOf(row);
          const errorId = error ? `${titleId}-${row.id}-error` : undefined;
          // A folded row: where it is and what it plans; a tap opens its fields (a row with an error or an open
          // change form stays open).
          if (openRow !== row.id && !error && changeFor !== row.id) {
            const subtopics = subtopicsOf(row);
            const when = [row.week ? t("folders.matrix.week_n", { n: row.week }) : null, row.hours !== "" ? t("folders.matrix.hours_n", { count: toNumber(row.hours) }) : null]
              .filter(Boolean)
              .join(" · ");
            return (
              <li key={row.id}>
                <button
                  type="button"
                  data-focus={`${row.id}:row`}
                  aria-expanded="false"
                  onClick={() => toggleRow(row.id, true)}
                  disabled={saving}
                  className={`flex w-full items-start gap-2 rounded-xl border border-slate-200 bg-white p-3 text-left transition duration-200 enabled:hover:border-slate-300 enabled:hover:bg-slate-50 ${focusRing}`}
                >
                  <span className="min-w-0 flex-1">
                    {when && <span className="block text-xs text-slate-500">{when}</span>}
                    {row.topic ? (
                      <>
                        <span className="mt-0.5 block break-words text-sm font-semibold text-slate-900">
                          {trimName(row.topic.name)}
                          {mark && <span className="font-normal text-slate-500"> · {mark}</span>}
                        </span>
                        <span className="mt-0.5 line-clamp-2 break-words text-xs text-slate-500">
                          {subtopics.length ? subtopics.map((s) => trimName(s.name)).join("; ") : t("folders.matrix.whole_topic")}
                        </span>
                      </>
                    ) : (
                      <span className="mt-0.5 flex items-center gap-1.5 text-sm font-semibold text-amber-800">
                        <span className="h-2 w-2 shrink-0 rounded-full bg-amber-500" aria-hidden="true" />
                        {t("folders.matrix.no_topic")}
                      </span>
                    )}
                    <span className="mt-1 block text-[13px] tabular-nums text-slate-600">
                      {levelsText(row.planned)} — {tasksAndPoints(t, row.planned, points)}
                    </span>
                  </span>
                  <ChevronDownIcon className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" />
                </button>
              </li>
            );
          }
          return (
            <li
              key={row.id}
              className={`scroll-mb-24 scroll-mt-[calc(var(--plan-bar,0px)+1rem)] space-y-3 rounded-xl border p-3 ${error ? "border-red-200 bg-red-50" : "border-blue-200 bg-white"}`}
            >
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  {error ? (
                    <RowName row={row} mark={mark} />
                  ) : (
                    <button
                      type="button"
                      data-focus={`${row.id}:row`}
                      aria-expanded="true"
                      onClick={() => toggleRow(row.id, false)}
                      className={`flex w-full items-start gap-2 rounded-lg text-left max-md:min-h-[2.75rem] ${focusRing}`}
                    >
                      <span className="min-w-0 flex-1">
                        <RowName row={row} mark={mark} expandable={false} />
                      </span>
                      <ChevronUpIcon className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" />
                    </button>
                  )}
                  {rowError(error, errorId)}
                  <div className="-ml-3 mt-1">{topicButton(row, name, errorId)}</div>
                </div>
                <button
                  type="button"
                  onClick={() => removeRow(row)}
                  disabled={saving}
                  aria-label={t("folders.matrix.remove_row", { name })}
                  className={iconButtonClass}
                >
                  <TrashIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
              {changeFor === row.id ? (
                changeForm(row)
              ) : (
                <>
                  {/* Two fields a line: the quarter and week, then the hours under the quarter. */}
                  <div className="grid grid-cols-2 gap-2">
                    {["col_quarter", "col_week"].map((key) => (
                      <span key={key} aria-hidden="true" className="text-xs font-medium text-slate-500">
                        {t(`folders.matrix.${key}`)}
                      </span>
                    ))}
                    {scheduleSelects(row, name, { large: true, error, errorId })}
                    <span aria-hidden="true" className="col-start-1 mt-1 text-xs font-medium text-slate-500">
                      {t("folders.matrix.col_hours")}
                    </span>
                    <div className="col-start-1">{hoursInput(row, name, { large: true, error, errorId })}</div>
                  </div>
                  <div className="space-y-2">
                    {DIFFICULTY_LEVELS.map((level) => (
                      <div key={level} className="flex items-center justify-between gap-3">
                        <DifficultyBadge level={level} />
                        <Stepper
                          large
                          value={row.planned[level]}
                          onChange={(value) => setCount(row.id, level, value)}
                          level={level}
                          name={name}
                          invalid={error?.field === "A"}
                          describedBy={errorId}
                          disabled={saving}
                          bank={row.topic ? (row.approved ? toNumber(row.approved[level]) : null) : undefined}
                          focusKey={level === DIFFICULTY_LEVELS[0] ? `${row.id}:A` : undefined}
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-slate-600">{tasksAndPoints(t, row.planned, points)}</p>
                </>
              )}
            </li>
          );
        })}
      </ul>
    );

  const totalsLine = (list) => {
    const planned = plannedOf(list);
    const assigned = editing ? 0 : list.reduce((sum, row) => sum + countOf(row.assigned), 0);
    return (
      <p className="flex flex-wrap items-center gap-x-5 gap-y-1 border-t border-slate-200 pt-3 text-sm text-slate-700">
        <b className="text-slate-900">{t("folders.matrix.totals_class", { grade: gradeLabel(t, selected) })}</b>
        {DIFFICULTY_LEVELS.map((level) => (
          <span key={level} className="tabular-nums">
            {level} <b className="text-slate-900">{toNumber(planned[level])}</b>
          </span>
        ))}
        <span className="tabular-nums">{tasksAndPoints(t, planned, points)}</span>
        {assigned > 0 && <span className="tabular-nums text-slate-500">{t("folders.matrix.assigned_total", { count: assigned })}</span>}
      </p>
    );
  };

  // The guide ("?" after the title, or "How it works" on the empty page) tells this reader's part on this screen.
  const emptyShown = !!matrix && !editing && savedRows.length === 0;
  const guide = (
    <MatrixGuide
      canEdit={matrix ? canEdit : canEditMatrix(folder?.role)}
      role={folder?.role}
      empty={emptyShown}
      template={template}
      standard={standard}
      points={points}
      copyable={copyable}
    />
  );

  let body;
  if (!matrix && (loading || !loadError)) {
    body = (
      <div role="status" className="flex items-center justify-center gap-3 px-6 py-16 text-sm text-slate-500">
        <Spinner />
        {t("folders.matrix.loading")}
      </div>
    );
  } else if (!matrix) {
    body = (
      <div className="px-4 py-10 sm:px-5">
        <div role="alert" className="mx-auto flex max-w-xl flex-wrap items-center justify-between gap-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          <span className="min-w-0">{getApiErrorMessage(t, loadError, "folders.matrix.load_failed")}</span>
          <Button text={t("folders.matrix.retry")} Icon={ArrowPathIcon} color="secondary-outline" onClick={load} loading={loading} />
        </div>
      </div>
    );
  } else if (!editing && savedRows.length === 0) {
    body = (
      <MatrixEmpty
        key={uuid}
        folderUuid={uuid}
        canEdit={canEdit}
        existing={toNumber(matrix.outside?.actual?.count)}
        grades={topics ? topicGrades : null}
        sources={sources}
        summaries={summaries}
        template={template}
        // Its moderator on a pre-v2 server learns why the matrix cannot be begun here.
        note={matrix.can_edit && !v2 ? t("folders.matrix.v1_note") : ""}
        onBegin={(g) => startEdit(g)}
        onBeginTemplate={(g) => startEdit(g, true)}
        onCopied={onCopied}
        onRetry={() => setSourcesAttempt((n) => n + 1)}
        help={
          <HelpButton id="matrix" variant="link" title={t("help.matrix.title")}>
            {guide}
          </HelpButton>
        }
      />
    );
  } else {
    const panel =
      selected === null ? (
        <p className="rounded-xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">{t("folders.matrix.empty_grades")}</p>
      ) : (
        <div
          role="tabpanel"
          id={`${tabsId}-panel`}
          aria-labelledby={`${tabsId}-tab-${selected}`}
          style={editing ? { "--plan-bar": `${planBarHeight}px` } : undefined}
          className={`space-y-5 ${editing ? focusClearance : ""}`}
        >
          {editing ? (
            <>
              {/* The negative offset (<main>'s padding) keeps the bar flush with the top, so no row shows above it. */}
              <div ref={planBarRef} className="sticky -top-4 z-10 -mx-1 bg-white/95 px-1 py-1 backdrop-blur lg:-top-6">
                <PlanBar planned={plannedOf(gradeRows)} standard={standard} points={points} compact={!wide} />
              </div>
              {noTopicLine}
              {rolesNote}
              {gaps.length > 0 && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="min-w-0 flex-1 basis-72">
                    <p className="text-sm font-semibold text-slate-900">
                      {t("folders.matrix.template_title", {
                        round: template.round,
                        weeks: t("folders.matrix.weeks_n", { count: template.weeks.length }),
                        levels: levelsText(plannedOf(template.weeks)),
                      })}
                    </p>
                    {/* A full matrix takes no more rows: then the hint says why the button waits. */}
                    <p id={`${titleId}-template-hint`} className="mt-0.5 text-xs text-slate-600">
                      {full ? t("folders.matrix.max_rows", { max: MAX_ROWS }) : t("folders.matrix.template_fill_hint", { count: gaps.length })}
                    </p>
                  </div>
                  <Button
                    text={t("folders.matrix.template_fill")}
                    color="secondary-outline"
                    onClick={() => fillTemplate(selected)}
                    disabled={saving || full}
                    aria-describedby={`${titleId}-template-hint`}
                    className="max-md:h-11 max-md:w-full"
                  />
                </div>
              )}
            </>
          ) : (
            <>
              <ClassSummary
                info={(matrix.grades || []).find((g) => g.grade === selected) || gradeInfoOf(gradeRows)}
                rows={gradeRows}
                standard={standard}
                points={points}
              />
              {noTopicLine}
              {rolesNote}
            </>
          )}
          {groups.map(({ quarter, rows: list }) => {
            const headingId = `${titleId}-q-${quarter || "none"}`;
            const formOpen = editing && addFor?.grade === selected && addFor?.quarter === quarter;
            return (
              <section key={quarter || "none"} aria-labelledby={headingId}>
                <QuarterHeading id={headingId} quarter={quarter} role={roles[quarter]} summary={quarterSummary(list)} />
                {list.length > 0 && (editing ? editRows(list) : readRows(list))}
                {editing &&
                  quarter &&
                  (formOpen ? (
                    <AddRowForm
                      grade={selected}
                      quarter={quarter}
                      folderUuid={uuid}
                      subjectUuid={subjectUuid}
                      rows={draft}
                      template={isSchoolGrade(selected) ? template : null}
                      topicsCache={topicsCache}
                      subtopicsCache={subtopicsCache}
                      full={full}
                      onSubmit={(spec) => addRow(spec, quarter)}
                      onClose={() => {
                        setAddFor(null);
                        setFocusTarget(`add:${quarter}`);
                      }}
                    />
                  ) : (
                    // A text link (no dashed box): an empty quarter shows only its heading and this line. Its "+" lines
                    // up with the heading.
                    <button
                      type="button"
                      data-focus={`add:${quarter}`}
                      onClick={() => {
                        setAddFor({ grade: selected, quarter });
                        setChangeFor(null);
                      }}
                      disabled={saving || full}
                      className={`${addLinkClass} -ml-2 ${list.length ? "mt-2" : ""}`}
                    >
                      <PlusIcon className="h-4 w-4" aria-hidden="true" />
                      {/* `context`: a language may word a quarter apart (ru «во II четверть»). */}
                      {t("folders.matrix.add_to_quarter", { quarter: ROMAN[quarter], context: String(quarter) })}
                    </button>
                  ))}
              </section>
            );
          })}
          {totalsLine(gradeRows)}
        </div>
      );
    const outsideCount = toNumber(matrix.outside?.actual?.count);
    body = (
      <div className="space-y-4 px-4 py-4 sm:px-5">
        {conflict && (
          <div
            data-focus="conflict"
            tabIndex={-1}
            role="alert"
            className="rounded-xl border border-amber-300 bg-amber-50 p-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500"
          >
            <p className="font-semibold text-amber-900">{t("folders.matrix.conflict_title")}</p>
            <p className="mt-1 text-sm text-amber-900">
              {conflict.changes.length ? t("folders.matrix.conflict_text") : t("folders.matrix.conflict_none")}
            </p>
            {conflict.changes.length > 0 && (
              <ul className="mt-2 list-disc space-y-0.5 pl-5 text-sm text-amber-900">
                {conflict.changes.slice(0, 8).map((change, index) => {
                  const name = `${topicName(t, change.row, markOf(change.row))} (${placeOf(change.row)})`;
                  const details = change.before ? changeDetails(t, change.before, change.row) : "";
                  return (
                    <li key={`${change.kind}-${rowKey(change.row)}-${index}`}>
                      {details ? t("folders.matrix.conflict_changed_detail", { name, details }) : t(`folders.matrix.conflict_${change.kind}`, { name })}
                    </li>
                  );
                })}
                {conflict.changes.length > 8 && <li>{t("folders.matrix.conflict_more", { count: conflict.changes.length - 8 })}</li>}
              </ul>
            )}
            {conflict.changes.length > 0 && (
              <p className="mt-2 text-sm font-medium text-amber-900">{t("folders.matrix.conflict_keep_note", { button: t("folders.matrix.conflict_keep") })}</p>
            )}
            {/* The panel holds the decision (the toolbar's Cancel and Save wait meanwhile): one main button, the other
                way out quiet. */}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button text={t("folders.matrix.conflict_keep")} color="primary" onClick={keepMine} loading={saving} className="max-md:h-11 max-md:w-full" />
              <button type="button" onClick={discardMine} disabled={saving} className={conflictQuietClass}>
                {t("folders.matrix.conflict_discard")}
              </button>
            </div>
          </div>
        )}
        {saveError && (
          <div data-focus="save-error" tabIndex={-1} role="alert" className={`rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ${focusRing}`}>
            {saveError}
          </div>
        )}
        <ClassTabs tabs={tabs} selected={selected} onSelect={(g) => setGrade(g)} idBase={tabsId}>
          {editing && <AddGradeMenu grades={addableGrades} onAdd={addGrade} disabled={saving} />}
          {editing && <span className="ml-auto text-xs text-slate-500 max-md:hidden">{t("folders.matrix.save_all_hint")}</span>}
        </ClassTabs>
        {panel}
        {!editing && outsideCount > 0 && (
          <p className="text-sm text-slate-600">
            <span className="font-medium text-slate-800">{t("folders.matrix.outside_line", { questions: t("folders.matrix.questions", { count: outsideCount }) })}</span>{" "}
            <span className="text-slate-500">{t("folders.matrix.outside_hint")}</span>
          </p>
        )}
      </div>
    );
  }

  // Cancel (quiet) and Save (the main button): in the header from md up, in a bar at the bottom of the screen on
  // phones. While a save conflict waits, its panel holds the decision instead.
  const editButtons = (
    <>
      <button type="button" onClick={cancel} disabled={saving} className={secondaryButtonClass}>
        <XMarkIcon className="h-4 w-4 max-md:hidden" aria-hidden="true" />
        {t("buttons.cancel")}
      </button>
      <Button
        text={t("buttons.save")}
        color="primary"
        onClick={() => save()}
        loading={saving}
        disabled={!dirty || !!conflict}
        className="max-md:h-11 max-md:flex-1"
      />
    </>
  );
  // Phones keep Cancel / Save in a bar at the bottom of the screen. While a row's form is open its own buttons finish
  // it first: the bar would cover them, and a Save there would leave the form's change out.
  const formShown = (!!addFor && addFor.grade === selected) || (!!changeFor && gradeRows.some((row) => row.id === changeFor));
  // "⋯": copying another folder's matrix of the subject over this one (while some has rows), a rare action that
  // replaces the whole matrix (its dialog says so). An empty matrix offers it on its own page instead. On phones the
  // edit mode puts it at the end of the title's row (`className`).
  const moreMenu = (className = "") => (
    <OverflowMenu
      label={t("folders.matrix.more_actions")}
      testId="matrix-actions"
      items={[copyable && { key: "copy", label: t("folders.matrix.copy_button"), Icon: DocumentDuplicateIcon, onClick: openCopy, disabled: saving }]}
      className={className}
    />
  );
  // Said by the header's live region (under the subtitle, shown there on phones); from md up a copy shows it beside
  // the buttons.
  const status = saving ? t("folders.matrix.saving") : dirty ? t("folders.matrix.unsaved") : "";

  let headerActions = null;
  if (editing && wide) {
    headerActions = (
      <div className="flex flex-wrap items-center justify-end gap-2">
        {status && (
          <span aria-hidden="true" className="text-xs text-slate-500">
            {status}
          </span>
        )}
        {!conflict && editButtons}
        {moreMenu()}
      </div>
    );
  } else if (!editing && canEdit && savedRows.length > 0) {
    // One main button; on phones it fills the row beside "⋯".
    headerActions = (
      <div className="flex items-center gap-2 max-md:w-full">
        <Button text={t("folders.matrix.edit")} Icon={PencilSquareIcon} color="primary" onClick={() => startEdit()} className="max-md:h-11 max-md:flex-1" />
        {moreMenu()}
      </div>
    );
  } else if (!editing && matrix?.can_edit && !v2 && savedRows.length > 0) {
    // A server before matrix v2: its rows would not take what this page saves (an empty matrix says it in its body).
    headerActions = <p className="max-w-sm text-xs text-slate-500">{t("folders.matrix.v1_note")}</p>;
  }

  return (
    <section ref={sectionRef} aria-labelledby={titleId} className={`${cardClass} min-w-0 max-w-full`}>
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3 border-b border-slate-100 px-4 py-3 sm:px-5">
        <div className="min-w-0 flex-1 basis-64">
          {/* The guide's "?" after the title, the row keeping the title's height (none while the matrix loads; the
              empty page has its "How it works" link instead). On phones the edit mode's "⋯" ends the row (its Cancel
              and Save are in the bar at the bottom): centred on the title's line, its framed 44px take the room under
              it, so the subtitle starts below the frame. */}
          <div className="flex min-w-0 items-start gap-1.5">
            <h2 ref={headingRef} id={titleId} tabIndex={-1} className="min-w-0 text-base font-semibold text-slate-900 outline-none">
              {editing ? t("folders.matrix.edit_title") : t("folders.matrix.title")}
            </h2>
            {(matrix ? !emptyShown : !loading) && (
              <HelpButton id="matrix" title={t("help.matrix.title")} className="-my-2.5 md:-my-1.5">
                {guide}
              </HelpButton>
            )}
            {editing && !wide && moreMenu("-mt-2.5 ml-auto")}
          </div>
          <p className="mt-0.5 text-sm text-slate-500">{t("folders.matrix.subtitle")}</p>
          {/* The save status as a live region in the DOM before it has anything to say (one that appears with its
              text may go unsaid): visible here on phones while it has text, hidden from md up (a copy shows it beside
              the buttons). */}
          {canEdit && (
            <p role="status" className={status && !wide ? "mt-1 text-xs font-medium text-slate-600" : "sr-only"}>
              {status}
            </p>
          )}
        </div>
        {headerActions}
      </div>
      {body}
      {editing && !wide && !formShown && !conflict && (
        <div className="sticky -bottom-4 z-20 flex gap-2 rounded-b-2xl border-t border-slate-200 bg-white px-4 py-3">{editButtons}</div>
      )}
      {canAssign && (
        <TaskDialog
          isOpen={!!taskRow}
          folder={folder}
          kind="write"
          row={taskRow}
          onClose={() => setTaskRow(null)}
          onSaved={(saved) => {
            setTaskRow(null);
            toast.success(t("folders.tasks.created", { name: [saved?.assignee?.first_name, saved?.assignee?.last_name].filter(Boolean).join(" ") || saved?.assignee?.username || "" }));
            load();
          }}
        />
      )}
      {canEdit && (
        <CopyMatrixDialog
          isOpen={copyOpen}
          folder={folder}
          folders={folders}
          summaries={summaries}
          rowsCount={savedRows.length}
          onClose={() => setCopyOpen(false)}
          onCopied={onCopied}
        />
      )}
    </section>
  );
}
